// DESCRIPTION: Dual-month calendar date range picker component. Renders two side-by-side
// month calendars with start/end date selection and an inline date display input.
import { __ } from '@wordpress/i18n';
import { useState, useRef, useEffect } from '@wordpress/element';
import {
	format,
	addMonths,
	subMonths,
	startOfMonth,
	endOfMonth,
	startOfWeek,
	endOfWeek,
	addDays,
	isSameDay,
	isSameMonth,
	isBefore,
	isAfter,
} from 'date-fns';

/**
 * Generates a grid of weeks (rows of 7 days) for a given month.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Date}   month        Any date within the target month.
 * @param {number} weekStartsOn 0=Sunday, 1=Monday.
 *
 * @return {Date[][]} Array of week arrays, each containing 7 Date objects.
 */
const getCalendarGrid = ( month, weekStartsOn = 0 ) => {
	const monthStart = startOfMonth( month );
	const monthEnd = endOfMonth( month );
	const gridStart = startOfWeek( monthStart, { weekStartsOn } );
	const gridEnd = endOfWeek( monthEnd, { weekStartsOn } );

	const weeks = [];
	let current = gridStart;

	while ( isBefore( current, gridEnd ) || isSameDay( current, gridEnd ) ) {
		const week = [];
		for ( let i = 0; i < 7; i++ ) {
			week.push( current );
			current = addDays( current, 1 );
		}
		weeks.push( week );
	}

	return weeks;
};

const DAY_LABELS = [ 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa' ];

/**
 * Renders a single month calendar grid.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}    props
 * @param {Date}      props.month      The month to display.
 * @param {Date|null} props.startDate  Selected start date.
 * @param {Date|null} props.endDate    Selected end date.
 * @param {Date|null} props.hoverDate  Currently hovered date (for range preview).
 * @param {Function}  props.onDayClick Called when a day is clicked.
 * @param {Function}  props.onDayHover Called when a day is hovered.
 *
 * @return {JSX.Element}
 */
const MonthGrid = ( {
	month,
	startDate,
	endDate,
	hoverDate,
	onDayClick,
	onDayHover,
} ) => {
	const weeks = getCalendarGrid( month );

	const isInRange = ( day ) => {
		if ( ! startDate ) {
			return false;
		}

		const rangeEnd = endDate || hoverDate;
		if ( ! rangeEnd ) {
			return false;
		}

		const from = isBefore( startDate, rangeEnd ) ? startDate : rangeEnd;
		const to = isBefore( startDate, rangeEnd ) ? rangeEnd : startDate;

		return (
			( isAfter( day, from ) || isSameDay( day, from ) ) &&
			( isBefore( day, to ) || isSameDay( day, to ) )
		);
	};

	const isRangeStart = ( day ) => startDate && isSameDay( day, startDate );
	const isRangeEnd = ( day ) => {
		const end = endDate || hoverDate;
		return end && isSameDay( day, end );
	};

	return (
		<div className="nemtly-month-grid">
			<div className="nemtly-month-title nb-text-center nb-font-semibold nb-text-neutral-900 nb-mb-3">
				{ format( month, 'MMM yyyy' ) }
			</div>
			<div className="nemtly-day-headers nb-grid nb-grid-cols-7 nb-mb-1">
				{ DAY_LABELS.map( ( label ) => (
					<div
						key={ label }
						className="nb-text-center nb-text-xs nb-font-semibold nb-text-neutral-500 nb-py-1"
					>
						{ label }
					</div>
				) ) }
			</div>
			<div className="nemtly-weeks">
				{ weeks.map( ( week, weekIndex ) => (
					<div key={ weekIndex } className="nb-grid nb-grid-cols-7">
						{ week.map( ( day ) => {
							const inCurrentMonth = isSameMonth( day, month );
							const isStart = isRangeStart( day );
							const isEnd = isRangeEnd( day );
							const selected = isStart || isEnd;
							const inRange = isInRange( day ) && ! selected;
							const today = isSameDay( day, new Date() );

							// Build wrapper class for range background
							let wrapperClass = 'nemtly-day-wrapper';
							if ( inRange && inCurrentMonth ) {
								wrapperClass += ' nemtly-day-in-range';
							}
							if (
								isStart &&
								inCurrentMonth &&
								( endDate || hoverDate )
							) {
								wrapperClass += ' nemtly-day-range-start';
							}
							if ( isEnd && inCurrentMonth ) {
								wrapperClass += ' nemtly-day-range-end';
							}

							// Build button classes
							let btnClass = 'nemtly-day-cell';
							if ( ! inCurrentMonth ) {
								btnClass += ' nemtly-day-outside';
							} else if ( selected ) {
								btnClass += ' nemtly-day-selected';
							} else if ( today ) {
								btnClass += ' nemtly-day-today';
							} else {
								btnClass += ' nemtly-day-default';
							}

							return (
								<div
									key={ day.toISOString() }
									className={ wrapperClass }
								>
									<button
										type="button"
										className={ btnClass }
										onClick={ () => onDayClick( day ) }
										onMouseEnter={ () => onDayHover( day ) }
									>
										{ format( day, 'd' ) }
									</button>
								</div>
							);
						} ) }
					</div>
				) ) }
			</div>
		</div>
	);
};

/**
 * DateRangePicker component.
 *
 * Dual-month calendar with start/end date selection.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {string}   props.startDate     Start date in YYYY-MM-DD format.
 * @param {string}   props.endDate       End date in YYYY-MM-DD format.
 * @param {Function} props.onStartChange Called with new start date string.
 * @param {Function} props.onEndChange   Called with new end date string.
 * @param {boolean}  props.hasError      Whether the field has a validation error.
 *
 * @return {JSX.Element}
 */
const DateRangePicker = ( {
	startDate,
	endDate,
	onStartChange,
	onEndChange,
	hasError = false,
} ) => {
	const [ isOpen, setIsOpen ] = useState( false );
	const [ viewingMonth, setViewingMonth ] = useState( () => {
		return startDate ? new Date( startDate + 'T00:00:00' ) : new Date();
	} );
	const [ selectionPhase, setSelectionPhase ] = useState( 'start' ); // 'start' or 'end'
	const [ hoverDate, setHoverDate ] = useState( null );
	const containerRef = useRef( null );

	const parsedStart = startDate ? new Date( startDate + 'T00:00:00' ) : null;
	const parsedEnd = endDate ? new Date( endDate + 'T00:00:00' ) : null;

	// Close on click outside
	useEffect( () => {
		const handleClickOutside = ( e ) => {
			if (
				containerRef.current &&
				! containerRef.current.contains( e.target )
			) {
				setIsOpen( false );
			}
		};

		if ( isOpen ) {
			document.addEventListener( 'mousedown', handleClickOutside );
		}

		return () =>
			document.removeEventListener( 'mousedown', handleClickOutside );
	}, [ isOpen ] );

	const handleDayClick = ( day ) => {
		const dateStr = format( day, 'yyyy-MM-dd' );

		if ( selectionPhase === 'start' ) {
			onStartChange( dateStr );
			onEndChange( '' );
			setSelectionPhase( 'end' );
		} else {
			// If clicked date is before start, swap: make it the new start
			if ( parsedStart && isBefore( day, parsedStart ) ) {
				onEndChange( startDate );
				onStartChange( dateStr );
			} else {
				onEndChange( dateStr );
			}
			setSelectionPhase( 'start' );
			setIsOpen( false );
		}
	};

	const handleDayHover = ( day ) => {
		if ( selectionPhase === 'end' ) {
			setHoverDate( day );
		}
	};

	const formatDisplayDate = ( dateStr ) => {
		if ( ! dateStr ) {
			return '';
		}

		const d = new Date( dateStr + 'T00:00:00' );
		return format( d, 'MMMM d, yyyy' );
	};

	const handleClear = ( e ) => {
		e.stopPropagation();
		onStartChange( '' );
		onEndChange( '' );
		setSelectionPhase( 'start' );
	};

	const nextMonth = addMonths( viewingMonth, 1 );

	return (
		<div
			className="nemtly-date-range-picker nb-relative"
			ref={ containerRef }
		>
			{ /* Inline display */ }
			<div
				className={ `nemtly-date-range-input nb-flex nb-items-center nb-border nb-rounded-md nb-px-3 nb-py-2 nb-cursor-pointer nb-bg-neutral-0 nb-transition-colors ${
					hasError
						? 'nb-border-danger-500'
						: isOpen
						? 'nb-border-neutral-500'
						: 'nb-border-neutral-300 hover:nb-border-neutral-500'
				}` }
				onClick={ () => setIsOpen( ! isOpen ) }
			>
				<span
					className={ `nb-text-sm ${
						startDate
							? 'nb-text-neutral-900'
							: 'nb-text-neutral-500'
					}` }
				>
					{ startDate
						? formatDisplayDate( startDate )
						: __( 'Select start date', 'nemtly-booking' ) }
				</span>
				<span className="nb-mx-3 nb-text-neutral-500">→</span>
				<span
					className={ `nb-text-sm ${
						endDate ? 'nb-text-neutral-900' : 'nb-text-neutral-500'
					}` }
				>
					{ endDate
						? formatDisplayDate( endDate )
						: __( 'Select end date', 'nemtly-booking' ) }
				</span>

				{ /* Clear button */ }
				{ ( startDate || endDate ) && (
					<button
						type="button"
						className="nb-ml-auto nb-text-neutral-500 hover:nb-text-neutral-700 nb-bg-transparent nb-border-0 nb-cursor-pointer nb-p-0.5"
						onClick={ handleClear }
						title={ __( 'Clear dates', 'nemtly-booking' ) }
					>
						<svg
							className="nb-w-4 nb-h-4"
							fill="currentColor"
							viewBox="0 0 20 20"
						>
							<path
								fillRule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
								clipRule="evenodd"
							/>
						</svg>
					</button>
				) }
			</div>

			{ /* Calendar dropdown */ }
			{ isOpen && (
				<div className="nemtly-date-range-calendar nb-absolute nb-z-50 nb-mt-1 nb-bg-neutral-0 nb-border nb-border-neutral-200 nb-rounded-lg nb-shadow-elevation-3 nb-p-4">
					{ /* Navigation */ }
					<div className="nb-flex nb-items-center nb-justify-between nb-mb-4">
						<div className="nb-flex nb-gap-1">
							<button
								type="button"
								className="nemtly-cal-nav-btn"
								onClick={ () =>
									setViewingMonth(
										subMonths( viewingMonth, 12 )
									)
								}
								title={ __(
									'Previous year',
									'nemtly-booking'
								) }
							>
								«
							</button>
							<button
								type="button"
								className="nemtly-cal-nav-btn"
								onClick={ () =>
									setViewingMonth(
										subMonths( viewingMonth, 1 )
									)
								}
								title={ __(
									'Previous month',
									'nemtly-booking'
								) }
							>
								‹
							</button>
						</div>
						<div className="nb-flex nb-gap-1">
							<button
								type="button"
								className="nemtly-cal-nav-btn"
								onClick={ () =>
									setViewingMonth(
										addMonths( viewingMonth, 1 )
									)
								}
								title={ __( 'Next month', 'nemtly-booking' ) }
							>
								›
							</button>
							<button
								type="button"
								className="nemtly-cal-nav-btn"
								onClick={ () =>
									setViewingMonth(
										addMonths( viewingMonth, 12 )
									)
								}
								title={ __( 'Next year', 'nemtly-booking' ) }
							>
								»
							</button>
						</div>
					</div>

					{ /* Selection hint */ }
					<div className="nb-text-center nb-text-xs nb-text-neutral-500 nb-mb-3">
						{ selectionPhase === 'start'
							? __( 'Select start date', 'nemtly-booking' )
							: __( 'Select end date', 'nemtly-booking' ) }
					</div>

					{ /* Dual month calendars */ }
					<div className="nb-flex nb-gap-6">
						<MonthGrid
							month={ viewingMonth }
							startDate={ parsedStart }
							endDate={ parsedEnd }
							hoverDate={ hoverDate }
							onDayClick={ handleDayClick }
							onDayHover={ handleDayHover }
						/>
						<MonthGrid
							month={ nextMonth }
							startDate={ parsedStart }
							endDate={ parsedEnd }
							hoverDate={ hoverDate }
							onDayClick={ handleDayClick }
							onDayHover={ handleDayHover }
						/>
					</div>
				</div>
			) }
		</div>
	);
};

export default DateRangePicker;
